import { AfterViewInit, Component, Input, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Subject } from "rxjs";
import { first } from "rxjs/operators";
import { TokenService } from "src/app/auth/token.service";
import { SavingStateService } from "src/app/core/survey/shared/services/saving-state.service";
import {
  SaveValidateInsideSurveyDto,
  ValidateInsideSurveyDto,
} from "src/app/shared/Models/write/ValidateSurveyDto";
import { ToastService } from "src/app/shared/toast.service";
import { ConfirmDialogComponent } from "../../../../../../shared/confirm-dialog/confirm-dialog.component";
import { SurveyStatus } from "../../../../../../shared/Models/enums/SurveySurveyStatus";
import { InsideSurvey } from "../../../../../../../app/shared/Models/read";
import { InsideSurveyService } from "../../../../../../shared/services/inside-survey.service";
import { ValidationError } from "../../../../../../shared/Models/ValidationError";
import { TsaRemovalService } from "src/app/shared/tsa-removal.service";
import { DocumentListService } from "../../../../../document-list/services/documentList.service";
import {
  DialogService,
  DialogType,
} from "src/app/shared/services/dialog.service";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";

@Component({
  selector: "app-inside-survey-validate",
  templateUrl: "./inside-survey-validate.component.html",
  styleUrls: ["./inside-survey-validate.component.scss"],
})
export class InsideSurveyValidateComponent implements AfterViewInit, OnDestroy {
  @Input()
  survey: InsideSurvey;
  @Input() getValidationError: () => ValidationError;
  remarks: string;
  surveyStatusEnum = SurveyStatus;

  constructor(
    private dialogService: DialogService,
    private readonly modalService: NgbModal,
    private readonly service: InsideSurveyService,
    private readonly router: Router,
    private savingStateService: SavingStateService,
    private toastService: ToastService,
    private tsaRemovalService: TsaRemovalService,
    private permissionsService: PermissionsService
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.remarks = this.survey.validatorRemark;
  }

  moveBackAfterSave() {
    this.survey.validatorRemark = this.remarks;
    this.router.navigateByUrl("/building-table");
  }

  private navigateToCommand() {
    this.router.navigateByUrl(
      "/admin/commands/" + this.survey.failedValidateSurveyCommandId
    );
  }

  public remarkChanged(event): void {
    const saveValidateSurveyDto = {
      surveyId: this.survey.id,
      surveyStatus: this.survey.surveyStatus,
      validatorRemark: this.remarks,
    } as SaveValidateInsideSurveyDto;
    this.savingStateService.addInsideSurveySaveValidation(
      saveValidateSurveyDto
    );
  }

  onValidate() {
    if (this.survey.buildings.find((t) => t.tsaId != null) != undefined) {
      var dialogText = this.tsaRemovalService.getRemarkForInsideSurvey(
        this.survey,
        $localize`:@@validation.insidesurvey.rejectsurvey:Are you sure you want to reject this survey?`
      );
      var deleteTsa: boolean = false;
      this.dialogService
        .openDialog({
          headerText: "Delete TSA",
          dialogType: DialogType.YesNo,
          detailText:
            "There's already a TSA for one of the buildings, do you want to Delete the current TSA record?",
        })
        .pipe(first())
        .subscribe((feedback) => {
          if (feedback) deleteTsa = true;

          var error = this.getValidationError();
          if (error) {
            this.toastService.showErrorToast(
              `${error.title}: ${error.sender}`,
              error.message
            );
            return;
          }
          var dto = new ValidateInsideSurveyDto({
            surveyId: this.survey.id,
            validatorRemark: this.remarks,
            deleteTSA: deleteTsa,
          });
          this.savingStateService
            .saveAll(true, this.service.validate(dto))
            .pipe(first())
            .subscribe();
        });
      return;
    } else {
      if (!navigator.onLine) {
        this.toastService.showWarnToast(
          $localize`:@@general.nointernet:No internet`,
          $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
        );
        return;
      }

      var error = this.getValidationError();
      if (error) {
        this.toastService.showErrorToast(
          `${error.title}: ${error.sender}`,
          error.message
        );
        return;
      }
      var dto = new ValidateInsideSurveyDto({
        surveyId: this.survey.id,
        validatorRemark: this.remarks,
        deleteTSA: false,
      });
      this.savingStateService
        .saveAll(true, this.service.validate(dto))
        .pipe(first())
        .subscribe();
    }
  }

  onReject() {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    var dialogText = this.tsaRemovalService.getRemarkForInsideSurvey(
      this.survey,
      $localize`:@@validation.insidesurvey.rejectsurvey:Are you sure you want to reject this survey?`
    );

    const modalRef = this.modalService.open(ConfirmDialogComponent);
    modalRef.componentInstance.message = dialogText;
    modalRef.componentInstance.decision
      .pipe(first())
      .subscribe((descision: boolean) => {
        if (descision)
          this.savingStateService
            .saveAll(true, this.service.reject(this.survey.id, this.remarks))
            .pipe(first())
            .subscribe();
      });
  }

  get disableValidate() {
    return this.survey.surveyStatus !== SurveyStatus.InsideWaitingForValidation;
  }

  get isSurveyBlockedByOngoingSync(): boolean {
    if (!this.survey.hasOngoingValidateSurvey) return false;
    return !this.permissionsService.allowed(PermissionsEnum.ForceRollBack);
  }

  get fullName() {
    return [
      this.survey?.insideEngineer?.firstName,
      this.survey?.insideEngineer?.lastName,
    ].join(" ");
  }
}
